import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useJwt } from 'react-jwt';
import html2pdf from 'html2pdf.js';
import { SideMenu } from '../user-home/SideMenu';
import { useIsValidToken } from '../../hooks/TokenHook';
import { Logout } from '../../../App';
import '../acceptance-letters/AcceptanceLetter.css';
import Background from '../../../assets/participation-certificate/background.jpeg';
import Signature from '../../../assets/participation-certificate/signature.png';
import Firma from '../../../assets/participation-certificate/firma.png';

const ATTENDANCE_GROUP = 'attendance-certificate';
const USER_INFO_URL =
  'https://nnsymposium.auth.us-west-1.amazoncognito.com/oauth2/userInfo';

const sanitizeFilename = (value) =>
  (value || 'certificate')
    .toString()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-zA-Z0-9-_ ]/g, '')
    .trim()
    .replace(/\s+/g, '_')
    .slice(0, 80) || 'certificate';

export const AttendanceCertificate = () => {
  const navigate = useNavigate();
  const id_token = localStorage.getItem('id_token');
  const { decodedToken } = useJwt(id_token);
  const isValidToken = useIsValidToken();
  const [isGenerating, setIsGenerating] = useState(false);
  const [userInfo, setUserInfo] = useState(null);

  useEffect(() => {
    if (!isValidToken) {
      Logout();
      navigate('/');
    }
  }, [isValidToken, navigate]);

  useEffect(() => {
    const accessToken = localStorage.getItem('access_token');
    if (!accessToken) return;
    fetch(USER_INFO_URL, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((info) => {
        if (info) setUserInfo(info);
      })
      .catch(() => {});
  }, []);

  const groups = (decodedToken && decodedToken['cognito:groups']) || [];
  const hasCertificate = groups.includes(ATTENDANCE_GROUP);
  const fullName =
    (userInfo && userInfo.name) ||
    (decodedToken && (decodedToken.name || decodedToken.email)) ||
    '';

  const DownloadPdf = async () => {
    const element = document.getElementById('attendance-printable');
    if (!element || isGenerating) return;

    setIsGenerating(true);
    try {
      const filename = `SNN2026_Attendance_${sanitizeFilename(fullName)}.pdf`;
      await html2pdf()
        .from(element)
        .set({
          margin: 0,
          filename,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: {
            scale: 2,
            useCORS: true,
            backgroundColor: '#ffffff',
            windowWidth: element.scrollWidth,
            windowHeight: element.scrollHeight,
          },
          jsPDF: { unit: 'in', format: [11, 8.5], orientation: 'landscape' },
        })
        .save();
    } catch (error) {
      alert('Error al generar el PDF. Intenta nuevamente.');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="snn-user-home-main-wrapper">
      <SideMenu />
      <div className="snn-abstract-submission-main-grid-wrapper">
        <div className="snn-expand-grid-4 acceptance-letter-grid-wrapper">
          <div className="acceptance-letter">
            {!decodedToken ? (
              <h1>Loading...</h1>
            ) : !hasCertificate ? (
              <div>
                <h1 className="snn-user-home-main-content-header">
                  No attendance certificate available yet
                </h1>
                <p>
                  Once the Organizing Committee issues your attendance certificate, it will be
                  available here for download. If you logged in before the certificate was issued,
                  please sign out and sign back in.
                </p>
              </div>
            ) : (
              <>
                <button
                  className="print-button"
                  onClick={DownloadPdf}
                  disabled={isGenerating}
                >
                  <span className="material-symbols-outlined">download</span>
                  {isGenerating ? 'Generating PDF…' : 'Download PDF'}
                </button>
                <div id="attendance-printable">
                  <div className="participation-cert-page">
                    <img src={Background} className="participation-cert-bg" alt="" />
                    <div className="participation-cert-content">
                      <h1 className="participation-cert-title">
                        The Organizing Committee of SNN 2026 certifies that:
                      </h1>
                      <p className="participation-cert-author">{fullName}</p>
                      <p className="participation-cert-subtitle">
                        Attended the{' '}
                        <span className="participation-cert-type">
                          Symposium of Nanoscience and Nanomaterials 2026
                        </span>
                        , held in Ensenada, B. C. Mexico from May 11 to 15, 2026.
                      </p>
                    </div>
                    <div className="participation-cert-footer">
                      <img src={Firma} className="participation-cert-signature" alt="Signature" />
                      <img src={Signature} className="participation-cert-signature" alt="Signature" />
                      <div className="participation-cert-signature-line">
                        ________________________________
                      </div>
                      <p className="participation-cert-chair">Dr. Uriel Caudillo Flores</p>
                      <p className="participation-cert-chair-role">SNN 2026 Chairman</p>
                      <p className="participation-cert-chair-date">
                        May 11 to 15, 2026 // Ensenada, B. C. Mexico
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
